import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import Heading from "../Heading";

interface ItemsModalViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const ItemsModalView: React.FC<ItemsModalViewProps> = ({ isOpen, onClose }) => {
  const { state } = useAppContext();
  const { isDarkMode } = useBrand();
  const { getCopy } = useCopy();

  // Transform empties from state for display
  const displayEmpties = state.empties.map(empty => ({
    id: empty.product.id,
    name: empty.product.name,
    size: empty.product.size,
    image: empty.product.image,
    quantity: empty.quantity,
    product: empty.product
  }));

  // Calculate total rewards
  const totalRewards = state.empties.reduce((totals, empty) => {
    if (empty.product.isBranded && empty.product.rewards) {
      totals.points += empty.product.rewards.loyaltyPoints * empty.quantity;
      // Parse dollar value (remove $ and convert to number)
      const dollarAmount = parseFloat(empty.product.rewards.dollarValue.replace('$', ''));
      totals.dollars += dollarAmount * empty.quantity;
    }
    return totals;
  }, { points: 0, dollars: 0 });

  // Check if there are any eligible items with rewards
  const hasEligibleItems = state.empties.some(empty => 
    empty.product.isBranded && empty.product.rewards
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: isOpen ? 0.3 : 0.3,
            ease: [0.25, 0.1, 0.25, 1]
          }}
          data-node-id="102:521"
        >
          {/* Background Overlay */}
          <motion.div
            className="absolute inset-0 backdrop-blur-lg"
            style={{ 
              backgroundColor: isDarkMode 
                ? 'rgba(23, 23, 23, 0.6)' 
                : 'rgba(250, 250, 250, 0.6)' 
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: isOpen ? 0.4 : 0.3,
              ease: [0.25, 0.1, 0.25, 1]
            }}
            onClick={onClose}
          />

          {/* Container that hugs both elements */}
          <div className="flex flex-col items-center max-w-[920px] w-full h-full">
            {/* Height Container - fills available space minus close button */}
            <div className="flex-1 w-full flex flex-col mb-8">
              {/* Main Container */}
              <motion.div
                className="relative rounded-[80px] w-full overflow-hidden"
                style={{
                  background: `linear-gradient(180deg, var(--brand-background) 80.05%, var(--brand-surface) 100%)`,
                  boxShadow: isDarkMode 
                    ? `0 20px 40px 0 rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)`
                    : `0 20px 40px 0 rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)`
                }}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={
                  isOpen 
                    ? {
                        type: "spring",
                        damping: 25,
                        stiffness: 200,
                        mass: 1,
                        delay: 0.1
                      }
                    : {
                        duration: 0.3,
                        ease: [0.25, 0.1, 0.25, 1]
                      }
                }
                data-node-id="125:1544"
              >
                {/* Content Container */}
                <div className="p-20 pt-28 pb-32">
              <motion.div
                className="flex flex-col gap-20 w-full max-w-[760px] mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={
                  isOpen 
                    ? {
                        duration: 0.6,
                        delay: 0.4,
                        ease: [0.25, 0.1, 0.25, 1]
                      }
                    : {
                        duration: 0.2,
                        ease: [0.25, 0.1, 0.25, 1]
                      }
                }
                data-node-id="125:1550"
              >
                {/* Header Section */}
                <Heading
                  title={getCopy('itemsModal.heading')}
                  subtitle={getCopy('itemsModal.subheading')}
                />

                {/* Items List Section */}
                <div className="flex flex-col gap-4">
                  {/* Table Header */}
                  <div className="flex flex-row items-start justify-between px-8 py-4 font-brand-primary font-medium text-[24px] leading-[32px] tracking-[-0.24px] text-brand-text-primary">
                    <div className="flex-1 text-left">
                      Empties you're returning
                    </div>
                    <div className="text-right">
                      Quantity
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="flex flex-col gap-6">
                    {displayEmpties.length === 0 ? (
                      <motion.div
                        className="bg-brand-background border border-brand-border rounded-3xl flex items-center justify-center py-16"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: 0.6,
                          ease: [0.25, 0.1, 0.25, 1]
                        }}
                      >
                        <p className="font-brand-primary font-light text-[24px] leading-[31px] text-brand-text-muted">
                          No empties added yet
                        </p>
                      </motion.div>
                    ) : (
                      displayEmpties.map((item, index) => (
                      <motion.div
                        key={item.id}
                        className="bg-brand-background border border-brand-border rounded-3xl flex flex-row gap-14 items-center justify-start pl-8 pr-10 py-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: 0.6 + (index * 0.1),
                          ease: [0.25, 0.1, 0.25, 1]
                        }}
                      >
                        {/* Product Image and Details */}
                        <div className="flex-1 flex flex-row gap-8 items-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                                <span className="text-blue-600 text-xs font-medium">
                                  {item.name.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-1">
                            <p className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary overflow-hidden text-ellipsis">
                              {item.name}
                            </p>
                            <p className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted">
                              {item.size}
                            </p>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary text-right w-[93px]">
                          {item.quantity}
                        </div>
                      </motion.div>
                      ))
                    )}
                  </div>
                </div>

                {/* Total Rewards Section - Only show if there are eligible items with rewards */}
                {hasEligibleItems ? (
                  <motion.div
                    className="flex flex-col gap-6 items-center justify-center px-8 py-0 w-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.8,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                  >
                    <div className="flex flex-col gap-2 items-center justify-center text-center">
                      <div className="font-brand-primary font-medium text-[24px] text-brand-text-primary">
                        <p className="block leading-[23px] text-nowrap whitespace-pre">Total Reward Value</p>
                      </div>
                      <div className="font-brand-primary font-light text-[18px] text-brand-text-muted">
                        <p className="block leading-[23px] text-nowrap whitespace-pre">Choose your reward method later</p>
                      </div>
                    </div>
                    <div className="flex flex-row items-start justify-start">
                      {/* Left section - Points */}
                        <div className="bg-brand-tertiary flex flex-row gap-4 items-center justify-center px-4 py-3 pl-6 rounded-bl-[100px] rounded-tl-[100px]">
                        <div className="font-brand-primary font-medium text-[32px] leading-[31px] text-white text-center text-nowrap whitespace-pre">
                          {totalRewards.points}
                        </div>
                      </div>
                      {/* Middle section - Dollar Value */}
                        <div className="bg-brand-tertiary flex flex-row gap-4 items-center justify-center px-4 py-3 relative border-l-[2px] border-r-[2px] border-brand-background">
                        <div
                          aria-hidden="true"
                          className="absolute border-brand-background border-[0px_2px] border-solid inset-0 pointer-events-none"
                        />
                        <div className="font-brand-primary font-medium text-[32px] leading-[31px] text-white text-center text-nowrap whitespace-pre">
                          ${totalRewards.dollars.toFixed(2)}
                        </div>
                      </div>
                      {/* Right section - Discount */}
                        <div className="bg-brand-tertiary flex flex-row gap-4 items-center justify-center px-4 py-3 pr-6 rounded-br-[100px] rounded-tr-[100px]">
                        <div className="font-brand-primary font-medium text-[32px] leading-[31px] text-white text-center w-[52px]">
                          {(() => {
                            const totalDiscount = state.empties.reduce((avg, empty) => {
                              if (empty.product.isBranded && empty.product.rewards) {
                                return avg + (parseInt(empty.product.rewards.discountValue) * empty.quantity);
                              }
                              return avg;
                            }, 0);
                            const eligibleItems = state.empties.reduce((total, empty) => {
                              if (empty.product.isBranded && empty.product.rewards) {
                                return total + empty.quantity;
                              }
                              return total;
                            }, 0);
                            const percentage = eligibleItems > 0 ? Math.round(totalDiscount / eligibleItems) : 0;
                            return `${percentage}%`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </motion.div>

            </div>
          </motion.div>
            </div>

            {/* Close Button - Positioned at bottom of hugging container */}
            <motion.div
              className="flex justify-center z-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={
                isOpen 
                  ? {
                      duration: 0.4,
                      delay: 0.7,
                      ease: [0.25, 0.1, 0.25, 1]
                    }
                  : {
                      duration: 0.2,
                      ease: [0.25, 0.1, 0.25, 1]
                    }
              }
            >
              <motion.button
                onClick={onClose}
                className="bg-brand-text-primary/10 rounded-full w-[88px] h-[88px] flex items-center justify-center hover:bg-brand-text-primary/20 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-node-id="102:525"
              >
                <X size={40} stroke="var(--brand-text-secondary)" strokeWidth={1.5} />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ItemsModalView;
